import { motion } from 'framer-motion';
import { Clock, Link, Shield, Target, TrendingUp, Users, Zap } from 'lucide-react';

const stats = [
  {
    value: '10',
    label: 'Integrated Security Products',
    description: 'All-in-one: GRC, IAM, CMDB, XDR, Service Desk, Pentest, Documents, Business, Business Continuity, and Data Governance',
    icon: Target,
    color: 'from-blue-500 to-blue-600',
  },
  {
    value: '95+',
    label: 'Native Integrations',
    description: 'Cloud (AWS/Azure/GCP), SIEMs, identity providers, vulnerability scanners, OT/ICS platforms, and threat intelligence feeds',
    icon: Link,
    color: 'from-green-500 to-green-600',
  },
  {
    value: '9',
    label: 'AI Voice Agents',
    description: '440+ specialized tools — ask any security question and get a real answer in seconds',
    icon: Zap,
    color: 'from-purple-500 to-purple-600',
  },
  {
    value: '24/7',
    label: 'Continuous Monitoring',
    description: 'Real-time threat detection and automated response across endpoints, cloud, identity, and OT networks',
    icon: Clock,
    color: 'from-orange-500 to-orange-600',
  },
  {
    value: 'Chain',
    label: 'Blockchain Audit Trail',
    description: 'Cryptographically immutable audit records on every risk, identity, and governance action — tamper-proof by design',
    icon: Shield,
    color: 'from-cyan-500 to-cyan-600',
  },
];

export function StatsSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-16"
          transition={{ duration: 0.6 }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-blue-500/30">
            <TrendingUp className="w-4 h-4 mr-2" />
            Platform Capabilities
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            The Cybersecurity ERP Effect
          </h2>

          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            One system replaces 8–12 point tools—connecting risk, identity, assets, threats, and compliance in a single platform that every team reads from.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={stat.label}
                viewport={{ once: true }}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 h-full hover:border-slate-600 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>

                  {/* Value */}
                  <div className="mb-2">
                    <span className={`text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                      {stat.value}
                    </span>
                  </div>

                  {/* Label */}
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {stat.label}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-400 text-sm">
                    {stat.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <motion.div
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 pt-12 border-t border-slate-700/50"
        >
          <p className="text-center text-slate-500 text-sm mb-8 uppercase tracking-wider">
            Compliance Frameworks We Support
          </p>

          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12">
            {/* SOC 2 Badge */}
            <div className="flex items-center gap-3 bg-slate-800/30 px-4 py-2 rounded-lg border border-slate-700/50">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>

              <div>
                <p className="text-white font-semibold text-sm">SOC 2</p>
                <p className="text-slate-500 text-xs">Ready</p>
              </div>
            </div>

            {/* ISO 27001 Badge */}
            <div className="flex items-center gap-3 bg-slate-800/30 px-4 py-2 rounded-lg border border-slate-700/50">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>

              <div>
                <p className="text-white font-semibold text-sm">ISO 27001</p>
                <p className="text-slate-500 text-xs">Ready</p>
              </div>
            </div>

            {/* GDPR Badge */}
            <div className="flex items-center gap-3 bg-slate-800/30 px-4 py-2 rounded-lg border border-slate-700/50">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>

              <div>
                <p className="text-white font-semibold text-sm">GDPR</p>
                <p className="text-slate-500 text-xs">Ready</p>
              </div>
            </div>

            {/* FedRAMP Badge */}
            <div className="flex items-center gap-3 bg-slate-800/30 px-4 py-2 rounded-lg border border-slate-700/50">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>

              <div>
                <p className="text-white font-semibold text-sm">FedRAMP</p>
                <p className="text-slate-500 text-xs">Ready</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default StatsSection;
